import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../../components/table/data-table";
import { useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { getModuleLabels } from "../../data/modules";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const PERMISSION_LIST_CACHE_KEY = "cehr_permission_list_v1";

const Permission = () => {
  const cachedData = useMemo(
    () => readSessionCache(PERMISSION_LIST_CACHE_KEY, { permissions: [] }),
    [],
  );
  const [permissions, setPermissions] = useState(cachedData?.permissions || []);
  const [isLoading, setIsLoading] = useState(!cachedData?.permissions?.length);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();

  const loadPermissions = async () => {
    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    try {
      if (permissions.length === 0) {
        setIsLoading(true);
      }
      setErrorMessage("");

      const response = await axios.get(
        `${apiBaseUrl}/organizations/permissions`,
      );
      const normalizedPermissions = (response?.data?.permissions || []).map(
        (item) => {
          const moduleIds = Array.isArray(item.components)
            ? item.components
            : item.component
              ? [item.component]
              : [];

          return {
            ...item,
            component: getModuleLabels(moduleIds),
          };
        },
      );

      setPermissions(normalizedPermissions);
      writeSessionCache(PERMISSION_LIST_CACHE_KEY, {
        permissions: normalizedPermissions,
      });
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to load permissions. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadPermissions();
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [permissions.length]);

  const handleViewDetails = (permission) => {
    navigate(
      {
        pathname: "/company-settings/permissionview-details",
        search: `?permissionId=${encodeURIComponent(permission?.id || "")}`,
      },
      {
        state: { permissionId: permission?.id },
      },
    );
  };

  const headings = ["Permission Name", "Component", "Description"];
  const columns = ["permissionName", "component", "description"];

  if (isLoading) {
    return <FetchLoadingState />;
  }

  return (
    <div className="p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Permissions" },
        ]}
        title="Permissions"
        subtitle="Create and manage permission sets for positions."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={() => navigate("/company-settings/new-permission")}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {errorMessage}
        </div>
      )}
      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={permissions}
        onView={handleViewDetails}
      />
    </div>
  );
};

export default Permission;
